/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Measure, { type ContentRect } from 'react-measure';

import type { Player, PlayerOptions } from 'video.js';

import { Grid } from '@coursera/cds-core';

import VideoJSPlayer from 'bundles/video-logged-out-page/components/video-player/VideoJSPlayer';
import { DEFAULT_VIDEO_WRAPPER_WIDTH } from 'bundles/video-logged-out-page/constants';
import type { VideoModel } from 'bundles/video-logged-out-page/types/vlpSharedTypes';
import { handleVideoJsError } from 'bundles/videojs/utils';

const styles = {
  videoJSPlayerWrapperDefaultContainer: css`
    max-width: 780px;
    margin: 0 auto;
    position: relative;
    padding-top: 56.25%; /* video is 16:9 ratio, this will retain that height while video loads in */
    overflow: hidden;
    background: var(--cds-color-neutral-primary);
  `,
  videoJSPlayerWrapperVideoWrapper: css`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  `,
};

export type PlayerEventHandlers = {
  onVideoEnded?: () => void;
  onVideoStarted?: () => void;
  onVideoSeeking?: () => void;
  onVideoPaused?: () => void;
  onVideoResumed?: () => void;

  onLoadedMetadata?: () => void;
  onTimeUpdate?: () => void;
  onDurationChange?: () => void;
};

export type Props = PlayerEventHandlers & {
  getPlayer?: (element: Player) => void;
  vjsOptions?: PlayerOptions;
  videoModel: VideoModel;
  // if you don't need to add a custom container for the video, add a default (at 16:9 ratio)
  // if adding a custom container around this component, be aware that the root element is absolute and you'll need to handle setting the ratio of the container element
  withDefaultContainer?: boolean;
  defaultQuality?: 'high';
};

type PropsToComponent = Props;

type State = {
  wrapperWidth: number;
};

class VideoPlayerWrapper extends React.Component<PropsToComponent, State> {
  declare _player: Player | null;

  declare _started: boolean;

  state = {
    wrapperWidth: DEFAULT_VIDEO_WRAPPER_WIDTH,
  };

  constructor(props: PropsToComponent) {
    super(props);

    this._player = null;
    this._started = false;
  }

  onMeasure = ({ bounds }: ContentRect) => {
    let wrapperWidth = bounds?.width || 0;
    if (wrapperWidth > DEFAULT_VIDEO_WRAPPER_WIDTH) {
      wrapperWidth = DEFAULT_VIDEO_WRAPPER_WIDTH;
    }
    this.setState({ wrapperWidth });
  };

  onEnded = () => {
    const { onVideoEnded } = this.props;

    if (onVideoEnded) {
      onVideoEnded();
    }
  };

  onStarted = () => {
    const { _started } = this;
    const { onVideoStarted, onVideoResumed } = this.props;

    if (!_started && onVideoStarted) {
      this._started = true;
      onVideoStarted();
    } else if (_started && onVideoResumed) {
      onVideoResumed();
    }
  };

  onSeeking = () => {
    const { onVideoSeeking } = this.props;

    if (onVideoSeeking) {
      onVideoSeeking();
    }
  };

  onPaused = () => {
    const { onVideoPaused } = this.props;

    if (onVideoPaused) {
      onVideoPaused();
    }
  };

  onError = () => {
    handleVideoJsError(this._player, 'VideoJSPlayer');
  };

  onLoadedMetadata = () => {
    const { onLoadedMetadata } = this.props;
    onLoadedMetadata?.();
  };

  onTimeUpdate = () => {
    const { onTimeUpdate } = this.props;
    onTimeUpdate?.();
  };

  onDurationChange = () => {
    const { onDurationChange } = this.props;
    onDurationChange?.();
  };

  getPlayer = (player: Player) => {
    const { getPlayer } = this.props;
    this._player = player;

    if (getPlayer) {
      getPlayer(this._player);
    }
  };

  render() {
    const { videoModel, vjsOptions = {}, withDefaultContainer, defaultQuality } = this.props;
    const { wrapperWidth } = this.state;

    const playerSettings = videoModel.getDefaultPlayerSettingByWrapperWidth(wrapperWidth);
    const videoComponent = (
      <Measure bounds onResize={this.onMeasure}>
        {({ measureRef }) => (
          <VideoJSPlayer
            videoRef={measureRef}
            eventListeners={{
              playing: this.onStarted,
              ended: this.onEnded,
              seeking: this.onSeeking,
              pause: this.onPaused,
              error: this.onError,
              loadedmetadata: this.onLoadedMetadata,
              timeupdate: this.onTimeUpdate,
              durationchange: this.onDurationChange,
            }}
            vjsOptions={{
              autoplay: 'play',
              ...vjsOptions,
            }}
            getPlayer={this.getPlayer}
            {...playerSettings}
            defaultQuality={defaultQuality}
          />
        )}
      </Measure>
    );

    return (
      <Grid>
        {withDefaultContainer ? (
          <div css={styles.videoJSPlayerWrapperDefaultContainer}>
            <div css={styles.videoJSPlayerWrapperVideoWrapper}>{videoComponent}</div>
          </div>
        ) : (
          <div css={styles.videoJSPlayerWrapperVideoWrapper}>{videoComponent}</div>
        )}
      </Grid>
    );
  }
}

export default VideoPlayerWrapper;
